'use client';

import { useAppLoader, useIntl } from '@capsicum/common';
import { Alert, Button, GenericDrawer, ModalService, useGoatImage } from '@capsicum/common-web';
import { AlertIcon } from '@capsicum/common-web/icons';
import { type NCOnboardingPageProps, useDeleteAccountScreen } from '@capsicum/features/nc-onboarding/shared';
import { DeleteAccountConfirmation } from '@capsicum/socios-core-web';
import { type ReactNode } from 'react';
import styled from 'styled-components';

import { HEADER_HEIGHT, NavigationMenu } from '../navigation-menu';

type DeleteAccountScreenProps = Pick<NCOnboardingPageProps, 'onBack' | 'onSuccess'>;

export const DeleteAccountScreen = ({ onBack, onSuccess }: DeleteAccountScreenProps) => {
	const { deleteAccount, privacyLink, supportLink } = useDeleteAccountScreen();
	const { $t } = useIntl();
	const loader = useAppLoader();
	const image = useGoatImage('surprise');

	const openSuccessDeletedAccountModal = () => {
		ModalService.addModal({
			onDismiss: onBack,
			renderComponent: ({ close }) => (
				<GenericDrawer
					buttonText={$t('global.button.close')}
					image={image}
					testID="settings-accountManagement-confirmation-modal"
					title={$t('settings.accountManagement.confirmation')}
					onButtonPress={close}
				>
					<S.Description>
						<S.Text3>
							{$t('settings.accountManagement.sociosDeletedAccount', {
								link: (chunks: Array<ReactNode>) => <S.Email href={supportLink}>{chunks}</S.Email>,
								strong: (chunks: Array<ReactNode>) => <S.Bold>{chunks}</S.Bold>
							})}
						</S.Text3>
					</S.Description>
				</GenericDrawer>
			)
		});
	};

	const deleteAccountHandler = async () => {
		await loader(deleteAccount());
		openSuccessDeletedAccountModal();
	};

	const openDeleteAccountModal = () => {
		ModalService.addModal({
			renderComponent: ({ close }) => (
				<DeleteAccountConfirmation
					close={close}
					onDeletePress={() => {
						deleteAccountHandler();
						onSuccess();
						close();
					}}
				/>
			)
		});
	};

	return (
		<>
			<NavigationMenu title={$t('settings.accountManagement.deleteAccount')} onClickBackButton={onBack} />
			<S.BackgroundView>
				<S.Wrapper>
					<Alert
						closable={false}
						icon={AlertIcon}
						subTitle={$t('settings.accountManagement.pleaseProceedWithCaution')}
						testID="settings-accountManagement-deleteAccount-alert"
						title={$t('settings.accountManagement.deletingYourAccountIsIrreversible')}
						type="warning"
					/>
					<S.Container>
						<S.ContentText>
							{$t('settings.accountManagement.deleteAccountContent', {
								link: (content: Array<ReactNode>) => (
									<S.PolicyLink href={privacyLink} rel="noreferrer" target="_blank">
										{content}
									</S.PolicyLink>
								)
							})}
						</S.ContentText>
						<Button
							color="destructive"
							fullWidth
							testID="settings-accountManagement-deleteAccount-button"
							text={$t('settings.accountManagement.deleteAccount')}
							onClick={openDeleteAccountModal}
						/>
						<Button
							color="neutral"
							fullWidth
							testID="settings-accountManagement-cancel-button"
							text={$t('global.button.cancel')}
							variant="text"
							onClick={onBack}
						/>
					</S.Container>
				</S.Wrapper>
			</S.BackgroundView>
		</>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: ${({ theme }) => `${theme.spacings.medium} 0`};
		gap: ${({ theme }) => theme.spacings.medium};

		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small}`};
		}
		max-width: 600px;
		margin: 0 auto;
	`;

	export const BackgroundView = styled.div`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
		height: calc(100vh - ${HEADER_HEIGHT}px);
	`;

	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.small};
		gap: ${({ theme }) => theme.spacings.small};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium}
	`;

	export const ContentText = styled.span`
		text-align: center;
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		white-space: pre-line;
	`;

	export const Description = styled.p`
		${({ theme }) => theme.fonts.text.text3};
		text-align: center;
		margin-top: ${({ theme }) => theme.spacings.xsmall};
		margin: 0;
		white-space: pre-line;
	`;

	export const Title = styled.span`
		${({ theme }) => theme.fonts.text.textTopBar}
	`;

	export const Text3 = styled.span`
		white-space: pre-line;
		text-align: center;
		${({ theme }) => theme.fonts.text.text3}
	`;

	export const Email = styled.a`
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;

	export const Bold = styled.span`
		${({ theme }) => theme.fonts.text.text1}
	`;

	export const PolicyLink = styled.a`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;
}
